import { Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DoingTabContent } from '@/components/doer/doing-tab-content'
import { SavedTabContent } from '@/components/favorites/saved-tab-content'
import { FirstLoginBanner } from '@/components/users/first-login-banner'
import { FeedTabContent } from '@/components/discovery/feed-tab-content'
import { useMyProjects } from '@/features/projects/use-projects'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import type { ProjectSummary } from '@/api/types'

const formatRelativeUpdated = (iso: string): string => {
  const updated = new Date(iso)
  const now = Date.now()
  const diff = now - updated.getTime()
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour
  if (diff < minute) return 'just now'
  if (diff < hour) return `${Math.floor(diff / minute)}m ago`
  if (diff < day) return `${Math.floor(diff / hour)}h ago`
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`
  return updated.toLocaleDateString()
}

const ProjectCard = ({ project }: { project: ProjectSummary }) => {
  const navigate = useNavigate()
  const url = canonicalProjectUrl(project.author.handle, project.slug)
  return (
    <Card
      className="cursor-pointer transition-colors hover:bg-accent"
      onClick={() => navigate(url)}
      role="link"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          navigate(url)
        }
      }}
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg">{project.title}</CardTitle>
          <Badge variant={project.visibility === 'PUBLIC' ? 'default' : 'secondary'}>
            {project.visibility === 'PUBLIC' ? 'Public' : 'Private'}
          </Badge>
        </div>
        <CardDescription>Updated {formatRelativeUpdated(project.updatedAt)}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="line-clamp-3 text-sm text-muted-foreground">
          {project.description ?? 'No description.'}
        </p>
      </CardContent>
    </Card>
  )
}

const SkeletonGrid = () => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <Skeleton key={i} className="h-48" />
    ))}
  </div>
)

const CreatedEmptyState = ({ onCreate }: { onCreate: () => void }) => (
  <Card>
    <CardContent className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <div>
        <h2 className="text-lg font-semibold">No projects yet.</h2>
        <p className="text-sm text-muted-foreground">Start your first build.</p>
      </div>
      <Button onClick={onCreate} className="gap-2">
        <Plus className="h-4 w-4" />
        New project
      </Button>
    </CardContent>
  </Card>
)

const CreatedTabContent = ({ onCreate }: { onCreate: () => void }) => {
  const { data, isLoading, isError, refetch, isFetching } = useMyProjects()
  const projects = data?.content ?? []

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Failed to load projects</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>We were unable to reach the server. Try again?</span>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  if (isLoading) return <SkeletonGrid />
  if (projects.length === 0) return <CreatedEmptyState onCreate={onCreate} />

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {projects.map((project) => (
        <ProjectCard key={project.id} project={project} />
      ))}
    </div>
  )
}

export const DashboardPage = () => {
  const navigate = useNavigate()
  const goToNew = () => navigate('/projects/new')

  return (
    <div className="space-y-6">
      <FirstLoginBanner />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Your stuff</h1>
          <p className="text-muted-foreground">
            Projects you authored and projects you&apos;re building.
          </p>
        </div>
        <Button onClick={goToNew} className="gap-2">
          <Plus className="h-4 w-4" />
          New project
        </Button>
      </div>

      <Tabs defaultValue="created" className="space-y-4">
        <TabsList>
          <TabsTrigger value="created">Created</TabsTrigger>
          <TabsTrigger value="doing">Doing</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="saved">Saved</TabsTrigger>
        </TabsList>
        <TabsContent value="created">
          <CreatedTabContent onCreate={goToNew} />
        </TabsContent>
        <TabsContent value="doing">
          <DoingTabContent />
        </TabsContent>
        <TabsContent value="subscriptions">
          <FeedTabContent />
        </TabsContent>
        <TabsContent value="saved">
          <SavedTabContent />
        </TabsContent>
      </Tabs>
    </div>
  )
}
